import {
  Dialog, DialogContent, Box, Typography, Button, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { COLORS, FONT_HEADING, BUTTON_HOVER_SX } from '../../talkTreeShared/Tree/constants/theme';

const BLUE = COLORS.primary;

const INTRO = 'Answer a few questions related to your students’ classroom talk to quickly create a class tree. No recording required.';

const STEPS = [
  {
    title: 'Move any slider to start growing your tree',
    body: 'Answer a few questions related to your students’ classroom talk to quickly create a class tree. No recording required.',
    tip: 'You can use the slider for a class activity by answering the questions together with your students.',
  },
  {
    title: 'Review your Talk Tree',
    body: 'As you adjust the sliders, your tree will grow and change to reflect the classroom talk patterns you described.',
    tip: 'Your Talk Tree can serve as a simple reflection tool to help track progress toward discussion goals, celebrate successes, and identify opportunities to increase student talk, reasoning, and collaboration.',
  },
  {
    title: 'Save your Talk Tree',
    body: 'Save your Talk Tree to capture a snapshot of your observation of your classroom discussion patterns. Give it a name you’ll remember.',
    tip: 'Comparing Talk Trees over time helps you track changes in student participation and measure the impact of your instructional strategies.',
  },
];

const BODY_SX = { fontSize: '15px', color: COLORS.text, lineHeight: 1.6 };

const StepNumber = ({ n }) => (
  <Box
    sx={{
      width: 46,
      height: 46,
      borderRadius: '50%',
      backgroundColor: COLORS.primaryLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>
      {n}
    </Typography>
  </Box>
);

export default function SimulatorHelpDialog({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="simulator-help-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          backgroundColor: COLORS.page,
          p: { xs: 2, sm: 3 },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{ position: 'absolute', right: 16, top: 16, color: '#111827', zIndex: 1 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          id="simulator-help-dialog-title"
          component="h2"
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: { xs: '26px', sm: '32px' },
            fontWeight: 400,
            color: BLUE,
            mb: 4,
            pr: 5,
          }}
        >
          How the tree simulator works
        </Typography>

        <Typography sx={{ ...BODY_SX, fontSize: '17px', mb: 3 }}>
          {INTRO}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {STEPS.map((step, i) => (
            <Box key={step.title} sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
              <StepNumber n={i + 1} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                  {step.title}
                </Typography>
                <Typography sx={{ ...BODY_SX, mb: 1 }}>{step.body}</Typography>
                <Typography sx={BODY_SX}>
                  <Typography component="span" sx={{ fontWeight: 700, color: '#111827', fontSize: 'inherit' }}>
                    Tip:
                  </Typography>{' '}
                  {step.tip}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            mt: 4,
            py: 1.4,
            px: 3,
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '4px',
            backgroundColor: COLORS.primaryDark,
            boxShadow: 'none',
            '&:hover': BUTTON_HOVER_SX,
          }}
        >
          I'm ready to answer questions
        </Button>
      </DialogContent>
    </Dialog>
  );
}